import { computed, getCurrentInstance, inject, unref } from 'vue-demi'
import { debugWarn, isClient } from '@xzx-design/utils'
import { useGetDerivedNamespace } from '../use-namespace'

import type { InjectionKey, Ref } from 'vue-demi'
import type { MaybeRef } from '@vueuse/core'

export type ElIdInjectionContext = {
  prefix: number
  current: number
}

const defaultIdInjection = {
  prefix: Math.floor(Math.random() * 10000),
  current: 0,
}

export const ID_INJECTION_KEY: InjectionKey<ElIdInjectionContext> =
  Symbol('elIdInjection')

export const useIdInjection = (): ElIdInjectionContext => {
  return getCurrentInstance()
    ? inject(ID_INJECTION_KEY, defaultIdInjection)
    : defaultIdInjection
}

export const useId = (deterministicId?: MaybeRef<string>): Ref<string> => {
  const idInjection = useIdInjection()
  if (!isClient && idInjection === defaultIdInjection) {
    debugWarn(
      'IdInjection',
      `Looks like you are using server rendering, you must provide a id provider to ensure the hydration process to be succeed
usage: app.provide(ID_INJECTION_KEY, {
  prefix: number,
  current: number,
})`
    )
  }

  const namespace = useGetDerivedNamespace()
  const idRef = computed(
    () =>
      unref(deterministicId) ||
      `${namespace.value}-id-${idInjection.prefix}-${idInjection.current++}`
  )

  return idRef
}

let current = 0

export function useBaseId() {
  const vm = getCurrentInstance()
  const { name = 'unknown' } = vm?.type || {}

  // keep test snapshot stable
  if (process.env.NODE_ENV === 'test') {
    return name
  }

  return `${name}-${++current}`
}

// Fix failed to get scopeId when using teleport & fragment
// https://github.com/vuejs/core/issues/2669
export const useScopeId = () => {
  const { scopeId } = getCurrentInstance()?.vnode || {}
  return scopeId ? { [scopeId]: '' } : null
}
